import React from "react";
import { Link } from "react-router-dom";
import { fillTemplate, getUiStrings } from "../seo/uiStrings";
import { useLocale } from "../hooks/useLocale";

interface PaginationProps {
    page: number;
    totalPages: number;
    /** Crawlable URL for a page number — see useCatalogPage(). */
    buildHref: (page: number) => string;
}

/**
 * Pages are real links, not buttons: each catalog page needs its own URL so crawlers
 * can reach past page 1 and so the position survives a reload or a shared link.
 */
export function Pagination({ page, totalPages, buildHref }: PaginationProps) {
    const { locale } = useLocale();
    const labels = getUiStrings(locale).pagination;

    if (totalPages <= 1) return null;

    const pages = buildPageNumbers(page, totalPages);

    return (
        <nav className="pagination" aria-label="Pagination">
            {page === 1 ? (
                <span className="pagination__btn pagination__btn--nav pagination__btn--disabled" aria-disabled="true">
                    <ChevronLeft />
                </span>
            ) : (
                <Link
                    to={buildHref(page - 1)}
                    className="pagination__btn pagination__btn--nav"
                    rel="prev"
                    aria-label={labels.previous}
                >
                    <ChevronLeft />
                </Link>
            )}

            {pages.map((p, i) =>
                p === "..." ? (
                    <span key={`ellipsis-${i}`} className="pagination__ellipsis">…</span>
                ) : (
                    <Link
                        key={p}
                        to={buildHref(p as number)}
                        className={`pagination__btn${p === page ? " pagination__btn--active" : ""}`}
                        aria-label={fillTemplate(labels.page, { n: p as number })}
                        aria-current={p === page ? "page" : undefined}
                    >
                        {p}
                    </Link>
                )
            )}

            {page === totalPages ? (
                <span className="pagination__btn pagination__btn--nav pagination__btn--disabled" aria-disabled="true">
                    <ChevronRight />
                </span>
            ) : (
                <Link
                    to={buildHref(page + 1)}
                    className="pagination__btn pagination__btn--nav"
                    rel="next"
                    aria-label={labels.next}
                >
                    <ChevronRight />
                </Link>
            )}
        </nav>
    );
}

function ChevronLeft() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    );
}

function ChevronRight() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
        </svg>
    );
}

function buildPageNumbers(current: number, total: number): (number | "...")[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages: (number | "...")[] = [1];

    if (current > 3) pages.push("...");

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);

    if (current < total - 2) pages.push("...");
    pages.push(total);

    return pages;
}
